import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  ActivatedRouteSnapshot,
  CanDeactivate,
  RouterStateSnapshot,
} from '@angular/router';
import { from, isObservable, map, Observable, of, switchMap } from 'rxjs';
import { RoleEnum } from 'server/shared/models/role.enum';
import { TranslationService } from '../../services/translation.service';
import { UserService } from '../../services/user.service';
import { ConfirmationDialog } from '../confirmation-dialog/confirmation.component';
import { UnsavedChangesDeactivate } from './unsaved-changes-deactivate.interface';

@Injectable({
  providedIn: 'root',
})
export class UnsavedChangesGuard
  implements CanDeactivate<UnsavedChangesDeactivate>
{
  constructor(
    private dialog: MatDialog,
    private translationService: TranslationService,
    private userService: UserService,
  ) {}

  canDeactivate(
    component: UnsavedChangesDeactivate,
    _currentRoute: ActivatedRouteSnapshot,
    _currentState: RouterStateSnapshot,
    _nextState?: RouterStateSnapshot,
  ): Observable<boolean> {
    const hasUnsavedChanges$ = component.hasUnsavedChanges();
    return of(null).pipe(
      switchMap(() =>
        isObservable(hasUnsavedChanges$)
          ? hasUnsavedChanges$
          : of(hasUnsavedChanges$),
      ),
      switchMap((hasUnsavedChanges: boolean) => {
        const warningDisabled =
          this.userService.user$.value?.role === RoleEnum.DISPO &&
          this.userService.user$.value?.disableUnsavedChangesWarning === true;
        if (!hasUnsavedChanges || warningDisabled) {
          return of(true);
        }
        return this.showConfirmationDialog();
      }),
    );
  }

  private showConfirmationDialog(): Observable<boolean> {
    const i18n = this.translationService.getTranslations();
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        id: 'unsaved-changes-dialog',
        title: i18n.unsavedChangesTitle,
        text: i18n.unsavedChangesText,
        cancelButtonLabel: i18n.back,
        confirmButtonLabel: i18n.discard,
        showDisableUnsavedChangesWarningCheckbox: true,
      },
    });

    return dialogRef.afterClosed().pipe(
      switchMap(
        (
          result:
            | boolean
            | string
            | { allow: true; disableUnsavedChangesWarning: boolean }
            | undefined,
        ) => {
          if (!result) {
            return of(false);
          }
          const disableUnsavedChangesWarning =
            typeof result === 'object' && result.disableUnsavedChangesWarning;
          if (disableUnsavedChangesWarning) {
            return from(
              new Promise<void>((resolve, reject) => {
                Meteor.call(
                  'updateDisableUnsavedChangesWarning',
                  true,
                  (err: Meteor.Error) => (err ? reject(err) : resolve()),
                );
              }),
            ).pipe(map(() => true));
          }
          return of(true);
        },
      ),
    );
  }
}
